import { createContext, useState, useCallback, useContext } from "react";
import { getBaseUrl } from "./config/apiConfig";
import { LoginContext } from "./LoginContext";
import { apiService } from "./backend/api";

export const MessagesContext = createContext();

export const MessagesProvider = ({ children }) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatCategory, setChatCategory] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [newCount, setNewCount] = useState(0); // Für neue Nachrichten (Circle)
  const [activeLiveChat, setActiveLiveChat] = useState(null); // { roomId, username }
  const { loginInfo } = useContext(LoginContext);
  const username = loginInfo?.username;

  // Füge eine neue Nachricht hinzu
  const addMessage = useCallback((message, category = "", senderType = "user", senderName = "") => {
    const newMessage = {
      id: Date.now(),
      sender: senderName || username || "user",
      senderType: senderType,
      subject: category || "Allgemeine Anfrage",
      content: message,
      date: new Date(),
      unread: false,
      conversationId: `conv_${Date.now()}`,
      category: category || "general_support",
      username: username || "user"
    };

    setChatMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, [username]);

  // Setze die Kategorie
  const setCategory = useCallback((category) => {
    setChatCategory(category);
  }, []);

  // Leere die Chat-Nachrichten
  const clearChatMessages = useCallback(() => {
    setChatMessages([]);
    setChatCategory("");
  }, []);

  // Hole alle Chat-Nachrichten
  const getChatMessages = useCallback(() => {
    return chatMessages;
  }, [chatMessages]);

  // Aktualisiere die Anzahl ungelesener Nachrichten
  const updateUnreadCount = useCallback(async () => {
    try {
      if (!username) return;

      const data = await apiService.request(`${getBaseUrl()}/api/messages/unread-count/${encodeURIComponent(username)}`);
      setUnreadCount(data.unreadCount);
    } catch (error) {
      console.error('Error updating unread count:', error);
    }
  }, []);

  // Aktualisiere die Anzahl NEUER Nachrichten (für Circle)
  const updateNewCount = useCallback(async () => {
    try {
      if (!username) return;

      const data = await apiService.request(`${getBaseUrl()}/api/messages/new-count/${encodeURIComponent(username)}`);
      setNewCount(data.newCount);
    } catch (error) {
      console.error('Error updating new count:', error);
    }
  }, []);

  // Markiere alle Nachrichten als "angesehen" (nicht mehr neu)
  const markAllAsViewed = useCallback(async () => {
    try {
      if (!username) return;

      await apiService.request(`${getBaseUrl()}/api/messages/mark-viewed/${encodeURIComponent(username)}`, {
        method: 'PUT'
      });

      setNewCount(0); // Circle verschwindet sofort
      // Aktualisiere auch die neue count nach kurzer Verzögerung
      setTimeout(() => {
        updateNewCount();
      }, 500);
    } catch (error) {
      console.error('Error marking messages as viewed:', error);
    }
  }, [updateNewCount]);

  // Markiere Nachricht als gelesen
  const markMessageAsRead = useCallback(async (messageId) => {
    try {
      await apiService.request(`${getBaseUrl()}/api/messages/mark-read/${messageId}`, {
        method: 'PUT'
      });

      // Aktualisiere die unread count
      updateUnreadCount();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [updateUnreadCount]);

  const value = {
    chatMessages,
    chatCategory,
    unreadCount,
    newCount,        // Neue Nachrichten count für Circle
    addMessage,
    setCategory,
    clearChatMessages,
    getChatMessages,
    updateUnreadCount,
    updateNewCount,  // Funktion zum Aktualisieren der neuen count
    markAllAsViewed, // Funktion zum Markieren als angesehen
    markMessageAsRead,
    activeLiveChat,
    setActiveLiveChat
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};

